import type { FieldProjectionSnapshot } from './client';
import { mergeRuntimeEntity, normalizedFieldReducer, type NormalizedFieldState, type VersionedRuntimeEntity } from './normalized-store';
import type { WorkerMobileProjection } from '../worker-mobile/model';

export interface WidgetRefreshSnapshot {
  base: NormalizedFieldState;
  snapshot: FieldProjectionSnapshot;
  mobileDevices: WorkerMobileProjection[] | null;
}

/** 조회 이후 수신된 항목은 유지하고 조회에서 사라진 기존 항목만 제거합니다. */
function mergeSnapshotMap(
  current: Record<string, VersionedRuntimeEntity>,
  base: Record<string, VersionedRuntimeEntity>,
  snapshot: Record<string, VersionedRuntimeEntity>
): Record<string, VersionedRuntimeEntity> {
  const merged = { ...snapshot };
  for (const [key, entity] of Object.entries(current)) {
    if (snapshot[key]) merged[key] = mergeRuntimeEntity(entity, snapshot[key]);
    else if (entity !== base[key]) merged[key] = entity;
  }
  return merged;
}

/** 수동 조회는 위젯에만 반영하며 공유 저장소·커서와 더 최신인 실시간 수신을 보존합니다. */
export function mergeWidgetSnapshot(state: NormalizedFieldState, update: WidgetRefreshSnapshot): NormalizedFieldState {
  const { base, snapshot, mobileDevices } = update;
  if (state.accessRevoked || state.fieldUid !== base.fieldUid || state.routingVersion !== base.routingVersion || state.ownershipEpoch !== base.ownershipEpoch) return state;
  let refreshed = normalizedFieldReducer(state, {
    type: 'SNAPSHOT_RECEIVED',
    fieldUid: state.fieldUid,
    provider: state.provider,
    routingVersion: state.routingVersion,
    ownershipEpoch: state.ownershipEpoch,
    ...snapshot,
  });
  // 조회 이후 모바일 응답 검증이 실패했다면 캐시된 정상 응답으로 숨김을 해제하지 않습니다.
  if (mobileDevices !== null && (state.mobileDevicesReady || state.mobileDevicesByRuntimeKey === base.mobileDevicesByRuntimeKey)) {
    refreshed = normalizedFieldReducer(refreshed, {
      type: 'MOBILE_SNAPSHOT_RECEIVED',
      fieldUid: state.fieldUid,
      routingVersion: state.routingVersion,
      ownershipEpoch: state.ownershipEpoch,
      mobileDevices,
    });
  }
  return {
    ...state,
    productsById: mergeSnapshotMap(state.productsById, base.productsById, refreshed.productsById),
    groupsById: mergeSnapshotMap(state.groupsById, base.groupsById, refreshed.groupsById),
    markersByRuntimeKey: mergeSnapshotMap(state.markersByRuntimeKey, base.markersByRuntimeKey, refreshed.markersByRuntimeKey),
    alarmsByRuntimeKey: mergeSnapshotMap(state.alarmsByRuntimeKey, base.alarmsByRuntimeKey, refreshed.alarmsByRuntimeKey),
    mobileDevicesByRuntimeKey: refreshed.mobileDevicesByRuntimeKey,
    mobileDevicesReady: refreshed.mobileDevicesReady,
    // 수동으로 다시 읽은 과거 알람을 새 실시간 알림으로 재생하지 않습니다.
    snapshotAlarmVersionsByRuntimeKey: { ...state.snapshotAlarmVersionsByRuntimeKey, ...refreshed.snapshotAlarmVersionsByRuntimeKey },
  };
}
